import { Injectable } from '@angular/core';
import {
  ModalController,
  LoadingController,
  AlertController,
  PopoverController,
  ActionSheetController,
  ToastController,
} from '@ionic/angular/standalone';

@Injectable({
  providedIn: 'root',
})
export class OverlayManagerService {
  constructor(
    private modalController: ModalController,
    private loadingController: LoadingController,
    private alertController: AlertController,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private toastController: ToastController
  ) {}

  /**
   * Cierra todos los overlays abiertos (modales, loadings, alerts, etc.)
   * Útil para limpiar el DOM cuando la sesión se cierra
   */
  async dismissAllOverlays(): Promise<void> {
    try {
      console.log('Iniciando cierre de todos los overlays...');

      // Usamos un approach secuencial para evitar conflictos
      // Primero cerramos los más críticos (loadings y modales)

      // 1. Cerrar loadings (prioritario para evitar bloqueo de UI)
      try {
        const topLoading = await this.loadingController.getTop();
        if (topLoading) {
          console.log('Cerrando loading activo...');
          await topLoading.dismiss();
        }
      } catch (e) {
        console.warn('Error cerrando loading:', e);
      }

      // 2. Cerrar modales (pueden contener otros overlays)
      try {
        const topModal = await this.modalController.getTop();
        if (topModal) {
          console.log('Cerrando modal activo...');
          await topModal.dismiss({ sessionExpired: true });
        }
      } catch (e) {
        console.warn('Error cerrando modal:', e);
      }

      // 3. Cerrar alertas
      try {
        const topAlert = await this.alertController.getTop();
        if (topAlert) {
          console.log('Cerrando alerta activa...');
          await topAlert.dismiss();
        }
      } catch (e) {
        console.warn('Error cerrando alerta:', e);
      }

      // 4. Cerrar popovers
      try {
        const topPopover = await this.popoverController.getTop();
        if (topPopover) {
          console.log('Cerrando popover activo...');
          await topPopover.dismiss();
        }
      } catch (e) {
        console.warn('Error cerrando popover:', e);
      }

      // 5. Cerrar action sheets
      try {
        const topActionSheet = await this.actionSheetController.getTop();
        if (topActionSheet) {
          console.log('Cerrando action sheet activo...');
          await topActionSheet.dismiss();
        }
      } catch (e) {
        console.warn('Error cerrando action sheet:', e);
      }

      // 6. Cerrar toasts
      try {
        const topToast = await this.toastController.getTop();
        if (topToast) {
          console.log('Cerrando toast activo...');
          await topToast.dismiss();
        }
      } catch (e) {
        console.warn('Error cerrando toast:', e);
      }

      console.log('Proceso de cierre de overlays completado');
    } catch (error) {
      console.error('Error general al cerrar overlays:', error);
    }
  }

  /**
   * Cierra específicamente todos los modales abiertos
   */
  async dismissAllModals(): Promise<void> {
    try {
      await this.modalController.dismiss();
      console.log('Todos los modales han sido cerrados');
    } catch (error) {
      console.error('Error al cerrar modales:', error);
    }
  }

  /**
   * Cierra específicamente todos los loadings abiertos
   */
  async dismissAllLoadings(): Promise<void> {
    try {
      await this.loadingController.dismiss();
      console.log('Todos los loadings han sido cerrados');
    } catch (error) {
      console.error('Error al cerrar loadings:', error);
    }
  }

  /**
   * Cierra específicamente todas las alertas abiertas
   */
  async dismissAllAlerts(): Promise<void> {
    try {
      await this.alertController.dismiss();
      console.log('Todas las alertas han sido cerradas');
    } catch (error) {
      console.error('Error al cerrar alertas:', error);
    }
  }

  /**
   * Verifica si hay algún overlay abierto
   * @returns Promise<boolean> true si hay al least un overlay abierto
   */
  async hasOpenOverlays(): Promise<boolean> {
    try {
      const modal = await this.modalController.getTop();
      const loading = await this.loadingController.getTop();
      const alert = await this.alertController.getTop();
      const popover = await this.popoverController.getTop();
      const actionSheet = await this.actionSheetController.getTop();
      const toast = await this.toastController.getTop();

      return !!(modal || loading || alert || popover || actionSheet || toast);
    } catch (error) {
      console.error('Error verificando overlays:', error);
      return false;
    }
  }

  /**
   * Método de limpieza completa para logout
   * Cierra todos los overlays y limpia el estado
   */
  async cleanupForLogout(): Promise<void> {
    console.log('Iniciando limpieza de overlays para logout...');

    try {
      // Primero verificamos si hay overlays abiertos
      const hasOverlays = await this.hasOpenOverlays();

      if (hasOverlays) {
        console.log('Overlays detectados, procediendo a cerrar...');
        await this.dismissAllOverlays();
      } else {
        console.log('No se encontraron overlays abiertos');
      }
    } catch (error) {
      console.error('Error durante la limpieza de logout:', error);
    }
  }
}
